import { Box, Flex, HStack, Image, Progress, Text } from "@chakra-ui/react";
import { CheckCircle2 } from "lucide-react";
import { STEPS, STATUS_CONFIG } from "../constants";
import PalommaLogoSmall from "@/assets/PalommaLogoSmall.jpg";

export function OnboardingSidebar({
  currentStep,
  isCompleted,
  applicationStatus,
}: {
  currentStep: number;
  isCompleted: boolean;
  applicationStatus: string;
}) {
  const progressValue = isCompleted ? 100 : (currentStep / STEPS.length) * 100;

  return (
    <>
      <Box display={{ base: "block", lg: "none" }} h="6px" w="full" bg="gray.100" flexShrink={0}>
        <Progress.Root value={progressValue} colorPalette="palomma" h="full">
          <Progress.Track h="full">
            <Progress.Range />
          </Progress.Track>
        </Progress.Root>
      </Box>

      <Box
        display={{ base: "none", lg: "flex" }}
        w="480px"
        maxW={{ base: "24rem", xl: "26rem" }}
        bg="palomma.800"
        color="white"
        position="relative"
        overflow="hidden"
        flexDir="column"
        flexShrink={0}
      >
        <Box
          position="absolute"
          top="-20%"
          left="-20%"
          w="140%"
          h="140%"
          borderRadius="full"
          background="linear-gradient(to bottom right, rgba(64, 18, 171, 0.2), transparent)"
          filter="blur(80px)"
          pointerEvents="none"
        />
        <Box
          position="absolute"
          bottom={0}
          right={0}
          w="75%"
          h="75%"
          borderRadius="full"
          background="linear-gradient(to top left, rgba(38, 10, 105, 0.4), transparent)"
          filter="blur(80px)"
          pointerEvents="none"
        />
        <Box
          position="absolute"
          inset={0}
          backgroundImage="linear-gradient(rgba(255,255,255,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.12) 1px, transparent 1px)"
          backgroundSize="32px 32px"
          opacity={0.2}
          pointerEvents="none"
        />

        <Box p={8} position="relative" zIndex={10} flexDir="column" display="flex" h="full">
          <HStack gap={3} mb={7}>
            <Box h={10} w={10} flexShrink={0} borderRadius="lg" bg="white/10" display="flex" alignItems="center" justifyContent="center">
              <Image src={PalommaLogoSmall} alt="Palomma" w="full" h="full" objectFit="contain" borderRadius="lg" />
            </Box>
            <Box>
              <Text fontSize="2xl" fontWeight="bold" letterSpacing="tight">Palomma</Text>
              <Text fontSize="sm" color="whiteAlpha.700">Portal de activación</Text>
            </Box>
          </HStack>

          <Box>
            <Text fontSize="4xl" fontWeight="bold" lineHeight="tall" maxW="xs">
              Activa tu recaudo sin perder de vista el proceso.
            </Text>
          </Box>

          <Box pt={12}>
            <Box borderRadius="lg" bg="white/10" p={5} display="flex" flexDir="column" h="full">
              <Flex justifyContent="space-between" alignItems="flex-start" gap={4} pb={4} borderBottom="1px solid" borderColor="whiteAlpha.200">
                <Box>
                  {isCompleted ? (
                    <>
                      <Text fontSize="xs" fontWeight="semibold" color="whiteAlpha.600" textTransform="uppercase" letterSpacing="wider">
                        {STATUS_CONFIG[applicationStatus]?.label ?? "Completado"}
                      </Text>
                      <Text fontSize="lg" fontWeight="semibold" mt={1}>
                        {STEPS[STEPS.length - 1].title}
                      </Text>
                      <Text fontSize="sm" color="whiteAlpha.700" mt={1}>
                        {STEPS[STEPS.length - 1].subtitle}
                      </Text>
                    </>
                  ) : (
                    <>
                      <Text fontSize="xs" fontWeight="semibold" color="whiteAlpha.600" textTransform="uppercase" letterSpacing="wider">
                        Paso actual
                      </Text>
                      <Text fontSize="lg" fontWeight="semibold" mt={1}>
                        {STEPS[Math.min(currentStep - 1, STEPS.length - 1)].title}
                      </Text>
                      <Text fontSize="sm" color="whiteAlpha.700" mt={1}>
                        {STEPS[Math.min(currentStep - 1, STEPS.length - 1)].subtitle}
                      </Text>
                    </>
                  )}
                </Box>
                <Box
                  borderRadius="full"
                  borderWidth="1px"
                  borderColor="whiteAlpha.200"
                  bg="whiteAlpha.100"
                  px={3}
                  py={1}
                  flexShrink={0}
                >
                  <Text fontSize="sm" fontWeight="medium">
                    {isCompleted ? STEPS.length : currentStep}/{STEPS.length}
                  </Text>
                </Box>
              </Flex>

              <Box mt={3} spaceY={2} flex="1">
                {STEPS.map((step) => {
                  const isActive = isCompleted ? false : currentStep === step.id;
                  const isPast = isCompleted ? true : currentStep > step.id;

                  return (
                    <HStack key={step.id} gap={3} alignItems="flex-start"
                      opacity={isActive ? 1 : isPast ? 0.7 : 0.4}
                      transition="opacity 0.3s" py={2}
                    >
                      <Box
                        mt={1} flexShrink={0} w={8} h={8} borderRadius="full" borderWidth="2px"
                        display="flex" alignItems="center" justifyContent="center" transition="colors 0.3s"
                        borderColor={isActive ? "white" : isPast ? "palomma.400" : "whiteAlpha.500"}
                        bg={isActive ? "whiteAlpha.200" : isPast ? "palomma.500" : "transparent"}
                        color={isActive ? "white" : isPast ? "white" : "whiteAlpha.500"}
                      >
                        {isPast ? (
                          <CheckCircle2 size={16} />
                        ) : (
                          <Text fontSize="sm" fontWeight="bold">{step.id}</Text>
                        )}
                      </Box>
                      <Box>
                        <Text fontWeight="bold" color="white" fontSize="sm">
                          {step.title}
                        </Text>
                        <Text fontSize="sm" color="whiteAlpha.600" mt={0.5}>
                          {step.subtitle}
                        </Text>
                      </Box>
                    </HStack>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
